const DB_NAME = 'avatar-chat-assets'
const DB_VERSION = 1
const STORE_NAME = 'assets'
const LS_KEY = 'avatar-chat-assets-fallback'

function createId(prefix = 'asset') {
  const rand = typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`
  return `${prefix}_${rand}`
}

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)
    reader.onerror = (err) => reject(err)
    reader.readAsDataURL(file)
  })
}

class LocalAssetStore {
  constructor() {
    this._dbPromise = null
    this._urlCache = new Map()
  }

  _isIndexedDBAvailable() {
    return typeof indexedDB !== 'undefined'
  }

  _openDb() {
    if (!this._isIndexedDBAvailable()) return Promise.resolve(null)
    if (this._dbPromise) return this._dbPromise

    this._dbPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION)
      request.onerror = () => reject(request.error)
      request.onsuccess = () => resolve(request.result)
      request.onupgradeneeded = () => {
        const db = request.result
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' })
          store.createIndex('kind', 'kind', { unique: false })
        }
      }
    })

    return this._dbPromise
  }

  async _withStore(mode, fn) {
    const db = await this._openDb()
    if (!db) return null
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, mode)
      const store = tx.objectStore(STORE_NAME)
      const result = fn(store)
      tx.oncomplete = () => resolve(result)
      tx.onerror = () => reject(tx.error)
      tx.onabort = () => reject(tx.error)
    })
  }

  async saveAsset(kind, file, metadata = {}) {
    if (!file) throw new Error('file is required')
    const id = createId(kind)
    const record = {
      id,
      kind,
      name: file.name || `${kind}-${id}`,
      type: file.type || 'application/octet-stream',
      size: file.size || null,
      createdAt: Date.now(),
      updatedAt: Date.now(),
      metadata: metadata || null
    }

    if (this._isIndexedDBAvailable()) {
      record.blob = file
      await this._withStore('readwrite', (store) => store.put(record))
      return record
    }

    const dataUrl = await readFileAsDataUrl(file)
    const existing = this._readFallback()
    existing.push({ ...record, dataUrl })
    this._writeFallback(existing)
    return { ...record, dataUrl }
  }

  async listAssets(kind = null) {
    if (this._isIndexedDBAvailable()) {
      const db = await this._openDb()
      if (!db) return []
      return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, 'readonly')
        const store = tx.objectStore(STORE_NAME)
        let request
        if (kind) {
          const index = store.index('kind')
          request = index.getAll(kind)
        } else {
          request = store.getAll()
        }
        request.onsuccess = () => resolve(request.result || [])
        request.onerror = () => reject(request.error)
      })
    }

    const items = this._readFallback()
    return kind ? items.filter((item) => item.kind === kind) : items
  }

  async getAsset(id) {
    if (!id) return null
    if (this._isIndexedDBAvailable()) {
      const db = await this._openDb()
      if (!db) return null
      return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, 'readonly')
        const store = tx.objectStore(STORE_NAME)
        const request = store.get(id)
        request.onsuccess = () => resolve(request.result || null)
        request.onerror = () => reject(request.error)
      })
    }

    const items = this._readFallback()
    return items.find((item) => item.id === id) || null
  }

  async deleteAsset(id) {
    if (!id) return false
    this._revokeUrl(id)

    if (this._isIndexedDBAvailable()) {
      await this._withStore('readwrite', (store) => store.delete(id))
      return true
    }

    const items = this._readFallback().filter((item) => item.id !== id)
    this._writeFallback(items)
    return true
  }

  _readFallback() {
    try {
      const raw = localStorage.getItem(LS_KEY)
      return raw ? JSON.parse(raw) : []
    } catch {
      return []
    }
  }

  _writeFallback(items) {
    try {
      localStorage.setItem(LS_KEY, JSON.stringify(items))
    } catch {
      /* ignore */
    }
  }

  _revokeUrl(id) {
    const existing = this._urlCache.get(id)
    if (existing) {
      try {
        URL.revokeObjectURL(existing)
      } catch {
        /* ignore */
      }
      this._urlCache.delete(id)
    }
  }

  getObjectUrl(record) {
    if (!record) return null
    if (record.dataUrl) return record.dataUrl
    if (this._urlCache.has(record.id)) return this._urlCache.get(record.id)
    if (record.blob) {
      const url = URL.createObjectURL(record.blob)
      this._urlCache.set(record.id, url)
      return url
    }
    return null
  }

  toOption(record) {
    if (!record) return null
    return {
      id: record.id,
      label: record.name || record.id,
      type: record.type || 'image',
      src: this.getObjectUrl(record),
      kind: record.kind,
      isLocal: true
    }
  }
}

export default new LocalAssetStore()
export { createId }
